import ls from 'localstorage-ttl';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { AuthorityType, UserData } from '@/types';
import { useFetchApi } from '@/composables/common/useFetchApi';

import { Authorization, type LoginBody } from '@/api/Authorization';
import type { ApiResponse } from '@/api/base';
import router from '@/router/router';
import { useToast } from 'vue-toastification';
import { useMasterStore } from './master';

// トークンの有効期限を30日に設定
const TOKEN_EXPIRY = 86400 * 30 * 1000; // 30

interface SessionResponse extends ApiResponse {
  token: string;
  user: UserData;
}

export const useAuthStore = defineStore('auth', () => {
  /**
   * state
   */
  const token = ref<string | null>(ls.get('tc_access_token'));
  const user = ref<UserData | null>(null);
  const sessionChecked = ref<boolean>(false);
  const isAuthenticated = ref<boolean>(false);

  /**
   * getter
   */
  const isLoggedIn = computed<boolean>(() => {
    return !!token.value && isAuthenticated.value && user.value !== null;
  });

  const userAuthority = computed<AuthorityType | undefined>(() => {
    return user.value?.authority;
  });

  const userName = computed<string>(() => {
    return `${user.value?.last_name || ''} ${user.value?.first_name || ''} `;
  });

  /**
   * private methods
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const _validateSessionResponse = (data: any): SessionResponse | null => {
    if (data) {
      if (typeof data?.token === 'string' && typeof data?.user === 'object') {
        const result: SessionResponse = {
          ...data
        };
        return result;
      }
    }
    return null;
  };

  const _clearAuth = async () => {
    ls.remove('tc_access_token');
    const masterStore = useMasterStore();
    await masterStore.removeMasterDataCache();
    isAuthenticated.value = false;
    token.value = null;
    user.value = null;
  };

  /**
   * actions
   */
  const session = async () => {
    if (sessionChecked.value && token.value && user.value) {
      return true;
    }

    // トークンがない場合は認証できない
    if (!token.value) {
      sessionChecked.value = true;
      await _clearAuth();
      return false;
    }

    const { fetchData, error, data } = useFetchApi();
    await fetchData(Authorization.checkSession);

    if (error.value) {
      await _clearAuth();
      throw error.value;
    }

    const response = _validateSessionResponse(data.value);
    if (response) {
      const userData: UserData = response.user;
      token.value = response.token;
      user.value = userData;
      sessionChecked.value = true;
      isAuthenticated.value = true;
      return true;
    } else {
      sessionChecked.value = true;
      await _clearAuth();
      return false;
    }
  };

  const login = async (requestData: LoginBody) => {
    const { fetchData, error, data } = useFetchApi();
    await fetchData(() => Authorization.login(requestData));

    if (error.value) {
      throw error.value;
    }

    const response = _validateSessionResponse(data.value);
    if (response) {
      const userData: UserData = response.user;

      ls.set('tc_access_token', response.token, TOKEN_EXPIRY);

      token.value = response.token;
      user.value = userData;
      sessionChecked.value = true;
      isAuthenticated.value = true;
    } else {
      console.error('Login API call succeeded but returned no data.');
    }
    return response;
  };

  const logout = async () => {
    const { fetchData } = useFetchApi();
    await fetchData(Authorization.logout);
    await _clearAuth();
  };

  const forceLogoutAndRedirect = async (message: string) => {
    await _clearAuth();
    const toast = useToast();
    // toast.error('再度ログインしてください', { timeout: 3000 }); // timeoutはvue-toastificationの正しいオプション名です
    toast.error(message, { timeout: 3000 });
    await router.push('/login');
  };

  return {
    token,
    user,
    sessionChecked,
    isAuthenticated,
    isLoggedIn,
    userAuthority,
    userName,
    _clearAuth,
    session,
    login,
    logout,
    forceLogoutAndRedirect
  };
});
